import React, { useState, useEffect } from 'react';
import pieceService from '../../../services/pieceService';
import MultiSelect from '../../common/MultiSelect.jsx';
import { useTheme } from '../../../contexts/ThemeContext';

const EditPieceModal = ({ isOpen, onClose, onSuccess, typeArticles, piece }) => {
  const { isDarkMode } = useTheme();
  const [designation, setDesignation] = useState('');
  const [famille, setFamille] = useState('');
  const [typeArticleIds, setTypeArticleIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingPiece, setLoadingPiece] = useState(false);

  // Fetch piece details when modal opens
  useEffect(() => {
    const fetchPiece = async () => {
      if (isOpen && piece?.id) {
        setLoadingPiece(true);
        setError('');
        try {
          const result = await pieceService.getPieceById(piece.id);
          if (result.success && result.data) {
            const p = result.data;
            setDesignation(p.designation || '');
            setFamille(p.famille || '');
            setTypeArticleIds(
              p.typeArticleId
                ? String(p.typeArticleId).split(',').filter(Boolean)
                : []
            );
          } else {
            setError(result.message || "Erreur lors de la récupération de la pièce");
            setDesignation('');
            setFamille('');
            setTypeArticleIds([]);
          }
        } catch {
          setError("Erreur lors de la récupération de la pièce");
          setDesignation('');
          setFamille('');
          setTypeArticleIds([]);
        } finally {
          setLoadingPiece(false);
        }
      }
      if (!isOpen) {
        setDesignation('');
        setFamille('');
        setTypeArticleIds([]);
        setError('');
        setLoading(false);
      }
    };
    fetchPiece();
    // eslint-disable-next-line
  }, [isOpen, piece]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        id: piece.id,
        designation,
        famille,
        typeArticleId: typeArticleIds.join(','),
      };
      const result = await pieceService.updatePiece(piece.id, payload);
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.message || "Erreur lors de la modification");
      }
    } catch {
      setError("Erreur lors de la modification");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className={`rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-primary-600 to-primary-700'}`}>
          <div className="flex items-center">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-3">
              <i className="fas fa-edit text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Modifier Pièce / Service</h2>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-primary-100'} text-sm`}>{piece?.designation}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>
        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {error && (
            <div className={`mb-4 p-3 rounded border ${isDarkMode ? 'bg-gray-900 border-gray-800 text-gray-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {error}
            </div>
          )}
          {loadingPiece ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>
              <i className="fas fa-spinner fa-spin mr-2"></i>Chargement...
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Désignation *</label>
                <input
                  type="text"
                  value={designation}
                  onChange={e => setDesignation(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                  required
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Famille *</label>
                <input
                  type="text"
                  value={famille}
                  onChange={e => setFamille(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
                  required
                />
              </div>
              <div>
                <MultiSelect
                  options={typeArticles}
                  value={typeArticleIds}
                  onChange={setTypeArticleIds}
                  label="Types d'articles"
                  placeholder="Sélectionner un ou plusieurs types"
                  icon="fas fa-tags"
                  required
                  getOptionValue={opt => String(opt.id)}
                  className={isDarkMode ? 'dark' : ''}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={onClose}
                  className={`px-4 py-2 rounded font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-4 py-2 rounded font-medium transition-colors ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : isDarkMode
                      ? 'bg-primary-700 text-white hover:bg-primary-800'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  {loading ? 'Modification...' : 'Modifier'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditPieceModal;